import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * SEO component to dynamically manage document head elements.
 * 
 * @param {Object} props
 * @param {string} props.title - The title of the page.
 * @param {string} props.description - The description of the page.
 * @param {string} [props.image] - Optional custom social image URL.
 * @param {string} [props.type] - Optional OpenGraph type (default: 'website').
 * @param {Object} [props.schema] - Optional JSON-LD structured data schema.
 */
export default function SEO({
  title,
  description,
  image = 'https://nerdcoach.it/og-image.jpg',
  type = 'website',
  schema = null
}) {
  const { pathname } = useLocation()
  const canonicalUrl = `https://nerdcoach.it${pathname}`

  useEffect(() => {
    // 1. Update Document Title
    const brandSuffix = 'Nerd Coach | Coaching per nerd, anime lover e gamer'
    const fullTitle = title ? `${title} | Nerd Coach` : brandSuffix
    document.title = fullTitle

    // Helper to select, create, or update meta tags
    const updateMetaTag = (attrName, attrValue, content) => {
      if (!content) return
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`)
      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attrName, attrValue)
        document.head.appendChild(element)
      }
      element.setAttribute('content', content)
    }

    // Helper to select, create, or update link tags
    const updateLinkTag = (rel, href) => {
      if (!href) return
      let element = document.querySelector(`link[rel="${rel}"]`)
      if (!element) {
        element = document.createElement('link')
        element.setAttribute('rel', rel)
        document.head.appendChild(element)
      }
      element.setAttribute('href', href)
    }

    // 2. Base Description
    const defaultDesc = 'Coaching mentale e fisico per nerd, anime lover, gamer e persone bloccate: focus, stress, energia, corpo e azione concreta.'
    const finalDesc = description || defaultDesc
    updateMetaTag('name', 'description', finalDesc)

    // 3. Canonical URL
    updateLinkTag('canonical', canonicalUrl)

    // 4. OpenGraph Core Tags
    updateMetaTag('property', 'og:title', fullTitle)
    updateMetaTag('property', 'og:description', finalDesc)
    updateMetaTag('property', 'og:type', type)
    updateMetaTag('property', 'og:url', canonicalUrl)
    updateMetaTag('property', 'og:image', image)
    updateMetaTag('property', 'og:site_name', 'Nerd Coach')
    updateMetaTag('property', 'og:locale', 'it_IT')

    // 5. Twitter Card Tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image')
    updateMetaTag('name', 'twitter:title', fullTitle)
    updateMetaTag('name', 'twitter:description', finalDesc)
    updateMetaTag('name', 'twitter:image', image)

    // 6. JSON-LD Structured Data Schema
    let schemaScript = document.getElementById('json-ld-seo')
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script')
        schemaScript.id = 'json-ld-seo'
        schemaScript.type = 'application/ld+json'
        document.head.appendChild(schemaScript)
      }
      schemaScript.text = JSON.stringify(schema)
    } else {
      if (schemaScript) {
        schemaScript.remove()
      }
    }
  }, [title, description, image, type, canonicalUrl, schema])

  return null
}
